/**
 * Super Admin Dashboard
 */

import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { Link } from 'react-router-dom';

export function SuperAdminDashboard() {
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    totalUsers: 0,
    totalDocuments: 0,
    signedDocuments: 0,
  });
  const [recentCompanies, setRecentCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Load companies
      const companiesSnapshot = await getDocs(collection(db, 'companies'));
      const companies = companiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const activeCompanies = companies.filter(c => c.active);

      // Load users
      const usersSnapshot = await getDocs(collection(db, 'users'));

      // Load documents (from requests collection)
      const docsSnapshot = await getDocs(collection(db, 'requests'));
      const docs = docsSnapshot.docs.map(doc => doc.data());
      const signedDocs = docs.filter(d => d.status === 'signed');

      // Get recent companies
      const recent = companies
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
        .slice(0, 5);

      setStats({
        totalCompanies: companies.length,
        activeCompanies: activeCompanies.length,
        totalUsers: usersSnapshot.size,
        totalDocuments: docs.length,
        signedDocuments: signedDocs.length,
      });

      setRecentCompanies(recent);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <p className="meta">Cargando dashboard...</p>
      </div>
    );
  }

  return (
    <div className="grid" style={{ gap: '24px' }}>
      {/* Header */}
      <div>
        <h1 className="title" style={{ fontSize: '28px', marginBottom: '8px' }}>Dashboard</h1>
        <p className="meta">Vista general del sistema multi-tenant</p>
      </div>

      {/* Stats Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
        gap: '16px' 
      }}>
        <StatCard
          icon="🏢"
          label="Empresas"
          value={stats.totalCompanies}
          subtitle={`${stats.activeCompanies} activas`}
        />
        <StatCard
          icon="👥"
          label="Usuarios"
          value={stats.totalUsers}
        />
        <StatCard
          icon="📄"
          label="Documentos"
          value={stats.totalDocuments}
          subtitle={`${stats.signedDocuments} firmados`}
        />
        <StatCard
          icon="✅"
          label="Tasa de Firma"
          value={`${stats.totalDocuments > 0 ? Math.round((stats.signedDocuments / stats.totalDocuments) * 100) : 0}%`}
        />
      </div>

      {/* Recent Companies */}
      <div className="card">
        <h2 className="title" style={{ fontSize: '20px', marginBottom: '16px' }}>
          Empresas Recientes
        </h2>
        <div className="grid" style={{ gap: '12px' }}>
          {recentCompanies.length === 0 ? (
            <p className="meta" style={{ textAlign: 'center', padding: '24px 0' }}>
              No hay empresas registradas
            </p>
          ) : (
            recentCompanies.map((company) => (
              <div 
                key={company.id} 
                style={{ 
                  padding: '12px',
                  background: 'rgba(79, 108, 247, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid var(--primary)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: '0 0 4px', fontSize: '16px' }}>{company.name}</h3>
                    <p className="meta">
                      {getCompanyTypeDisplay(company.type)} • 
                      {company.active ? ' ✅ Activa' : ' ⏸️ Inactiva'}
                    </p>
                  </div>
                  <Link to={`/super-admin/companies/${company.id}`} className="copy">
                    Ver →
                  </Link>
                </div>
              </div>
            ))
          )}
        </div>
        {recentCompanies.length > 0 && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--primary)' }}>
            <Link to="/super-admin/companies" className="copy" style={{ display: 'inline-block' }}>
              Ver todas las empresas →
            </Link>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
        <QuickActionCard
          icon="🏢"
          title="Crear Nueva Empresa"
          description="Añadir una nueva empresa al sistema"
          to="/super-admin/companies/new"
        />
        <QuickActionCard
          icon="👥"
          title="Gestionar Usuarios"
          description="Ver y administrar todos los usuarios"
          to="/super-admin/users"
        />
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, subtitle }) {
  return (
    <div className="card" style={{ padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{ 
          fontSize: '32px',
          background: 'var(--primary)',
          borderRadius: '12px',
          padding: '12px',
          lineHeight: 1
        }}>
          {icon}
        </div>
        <div style={{ flex: 1 }}>
          <p className="kicker" style={{ marginBottom: '4px' }}>{label}</p>
          <p style={{ fontSize: '28px', fontWeight: 'bold', margin: 0 }}>{value}</p>
          {subtitle && (
            <p className="meta" style={{ marginTop: '4px' }}>{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function QuickActionCard({ icon, title, description, to }) {
  return (
    <Link
      to={to}
      className="card"
      style={{ 
        display: 'block',
        padding: '20px',
        textDecoration: 'none',
        transition: 'transform 0.2s, box-shadow 0.2s'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 108, 247, 0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
        <span style={{ fontSize: '36px', lineHeight: 1 }}>{icon}</span>
        <div>
          <h3 style={{ margin: '0 0 8px', fontSize: '18px' }}>{title}</h3>
          <p className="meta">{description}</p>
        </div>
      </div>
    </Link>
  );
}

function getCompanyTypeDisplay(type) {
  const types = {
    'albaran': 'Albarán',
    'parte_trabajo': 'Parte de Trabajo',
    'custom': 'Personalizado'
  };
  return types[type] || type;
}

export default SuperAdminDashboard;
